import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Undo2 } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/shared/components/ui/sheet';
import { useToast } from '@/shared/components/ui/use-toast';
import { DataPagination } from '@/shared/components/DataPagination';
import { usePagination } from '@/shared/hooks/usePagination';
import { formatDateTimeBR } from '@/shared/utils/datetime';
import { resolveApiErrorMessage } from '@/shared/utils/apiErrorMessage';
import { BrainService } from '../services/brainService';
import type { MindBlockSnapshot, MindChange } from '../types/BrainTypes';

interface MindChangesDialogProps {
  workspaceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canUndo: boolean;
  /** Depois de desfazer, a Mind precisa ser recarregada. */
  onUndone: () => void;
}

function snapshotText(snapshot: MindBlockSnapshot | null): string | null {
  if (!snapshot) return null;
  const value = snapshot.content?.value;
  return typeof value === 'string' ? value : JSON.stringify(snapshot.content ?? {}, null, 2);
}

/** A trilha da Mind (AB#86817, AC8): autor, origem, antes e depois, e desfazer. */
export const MindChangesDialog = ({ workspaceId, open, onOpenChange, canUndo, onUndone }: MindChangesDialogProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [undoing, setUndoing] = useState<string | null>(null);

  const { data: changes = [], isLoading } = useQuery({
    queryKey: ['mind-changes', workspaceId],
    queryFn: () => BrainService.getMindChanges(workspaceId),
    enabled: open,
  });
  const { currentPage, pageSize, setCurrentPage, setPageSize, paginateData } = usePagination<MindChange>();

  const undo = async (change: MindChange) => {
    setUndoing(change.id);
    try {
      await BrainService.undoMindChange(workspaceId, change.id);
      await queryClient.invalidateQueries({ queryKey: ['mind-changes', workspaceId] });
      toast({ title: t('knowledge.mind.undoSuccess') });
      onUndone();
    } catch (error) {
      toast({
        title: t('knowledge.mind.undoError'),
        description: resolveApiErrorMessage(error, t),
        variant: 'destructive',
      });
    } finally {
      setUndoing(null);
    }
  };

  return (
    // Painel lateral à direita: a lista abre por cima da Mind, sem tomar a tela toda.
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-4 sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>{t('knowledge.mind.changesTitle')}</SheetTitle>
          <SheetDescription>{t('knowledge.mind.changesDescription')}</SheetDescription>
        </SheetHeader>

        {isLoading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : changes.length === 0 ? (
          <p className="py-6 text-sm text-muted-foreground">{t('knowledge.mind.noChanges')}</p>
        ) : (
          <>
          <ul className="min-h-0 flex-1 space-y-2 overflow-y-auto pr-1">
            {paginateData(changes).map((change) => {
              const title = change.after?.title ?? change.before?.title ?? change.field_name;
              const isOpen = expanded === change.id;
              return (
                <li key={change.id} className="rounded-md border">
                  <div className="flex items-center gap-3 px-3 py-2">
                    <button
                      type="button"
                      className="flex flex-1 flex-wrap items-center gap-2 text-left text-sm"
                      onClick={() => setExpanded(isOpen ? null : change.id)}
                    >
                      <span className="font-medium">{t(`knowledge.mind.actions.${change.action}`)}</span>
                      <span className="truncate">{title}</span>
                      <Badge variant="outline" className="text-[10px]">
                        {t(`knowledge.mind.sources.${change.source}`)}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {formatDateTimeBR(change.created_at)}
                        {change.actor ? ` · ${change.actor.full_name || change.actor.email}` : ''}
                      </span>
                    </button>
                    {change.undone_by_change_id ? (
                      <Badge variant="secondary" className="text-[10px]">
                        {t('knowledge.mind.undone')}
                      </Badge>
                    ) : (
                      canUndo && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => undo(change)}
                          disabled={undoing !== null}
                        >
                          {undoing === change.id ? (
                            <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Undo2 className="mr-1 h-3.5 w-3.5" />
                          )}
                          {t('knowledge.mind.undo')}
                        </Button>
                      )
                    )}
                  </div>
                  {isOpen && (
                    <div className="grid grid-cols-2 gap-2 border-t p-3">
                      {(['before', 'after'] as const).map((side) => (
                        <div key={side} className="min-w-0">
                          <p className="mb-1 text-xs font-medium text-muted-foreground">{t(`knowledge.mind.${side}`)}</p>
                          <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded bg-muted p-2 text-xs">
                            {snapshotText(change[side]) ?? t('knowledge.mind.removed')}
                          </pre>
                        </div>
                      ))}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          <DataPagination
            currentPage={currentPage}
            pageSize={pageSize}
            totalItems={changes.length}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
          />
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
